import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, LogIn, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { beep } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { ChatPanel } from "./ChatPanel";

export function ChatWidget() {
  const { user } = useAuth();
  const { t } = useContent();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pos, setPos] = useState({ x: null, y: null });
  const drag = useRef({ active: false, moved: false });
  const prevUnread = useRef(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const poll = () => { if (!open) api.get("/support/sync").then((r) => {
      const u = r.data.unread;
      if (u > prevUnread.current) beep();
      prevUnread.current = u;
      setUnread(u);
    }).catch(() => {}); };
    poll(); const t = setInterval(poll, 8000); return () => clearInterval(t);
  }, [open, user]);

  useEffect(() => {
    const move = (e) => {
      if (!drag.current.active) return;
      const ev = e.touches ? e.touches[0] : e;
      const dx = ev.clientX - drag.current.ox, dy = ev.clientY - drag.current.oy;
      if (Math.abs(dx) + Math.abs(dy) > 5) drag.current.moved = true;
      setPos({ x: Math.min(window.innerWidth - 68, Math.max(8, drag.current.sx + dx)), y: Math.min(window.innerHeight - 76, Math.max(8, drag.current.sy + dy)) });
    };
    const up = () => { if (!drag.current.active) return; const moved = drag.current.moved; drag.current.active = false; if (!moved) setOpen((o) => !o); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move); window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up); };
  }, []);

  const onDown = (e) => {
    const ev = e.touches ? e.touches[0] : e;
    drag.current = { active: true, moved: false, ox: ev.clientX, oy: ev.clientY,
      sx: pos.x ?? (window.innerWidth - 76), sy: pos.y ?? (window.innerHeight - 84) };
  };
  const btnStyle = pos.x == null ? { right: 20, bottom: 24 } : { left: pos.x, top: pos.y };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[70] flex h-[100dvh] w-full flex-col overflow-hidden rounded-none border border-border bg-card shadow-2xl sm:inset-auto sm:bottom-24 sm:right-5 sm:h-[70vh] sm:max-h-[560px] sm:w-[calc(100vw-2.5rem)] sm:max-w-sm sm:rounded-2xl" data-testid="chat-window">
          <div className="flex items-center justify-between border-b border-border bg-primary px-4 py-3 text-primary-foreground">
            <div>
              <div className="font-display text-sm font-bold">{t("chat.title", "Support en direct")}</div>
              <div className="text-[11px] opacity-80">The World Promotions</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="-mr-1 rounded-lg p-2 hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
          <div className="min-h-0 flex-1">
            {user
              ? <ChatPanel onSynced={() => setUnread(0)} />
              : (
                <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center" data-testid="chat-guest-prompt">
                  <MessageCircle className="h-10 w-10 text-accent" />
                  <p className="text-sm text-muted-foreground">{t("chat.guest_prompt", "Connectez-vous ou créez un compte pour démarrer une conversation avec notre support.")}</p>
                  <div className="flex w-full flex-col gap-2">
                    <Link to="/login" onClick={() => setOpen(false)} data-testid="chat-guest-login" className="flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-accent"><LogIn className="h-4 w-4" /> {t("chat.login", "Se connecter")}</Link>
                    <Link to="/register" onClick={() => setOpen(false)} data-testid="chat-guest-register" className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary"><UserPlus className="h-4 w-4" /> {t("chat.register", "Créer un compte")}</Link>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
      <button style={{ position: "fixed", zIndex: 70, ...btnStyle }} onMouseDown={onDown} onTouchStart={onDown} data-testid="chat-widget-btn"
        className={`${open ? "hidden sm:flex" : "flex"} h-12 w-12 cursor-grab items-center justify-center rounded-full bg-accent text-white shadow-xl transition-transform hover:scale-105 active:cursor-grabbing sm:h-14 sm:w-14`}>
        <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />
        {unread > 0 && <span data-testid="chat-unread-badge" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold">{unread}</span>}
      </button>
    </>
  );
}
